import { Box, Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { UserAnswers } from '../../types';

import MonthlyExpenseCard from './MonthlyExpenseCard';
import MixedUseExpenseModal from './MixedUseExpenseModal';
import ReimbursementSummary from './ReimbursementSummary';

import { useMember } from 'modules/common/queries/member';

type MonthlyData = {
    [month: string]: MonthlyExpenseData;
};

type MonthlyExpenseData = {
    homeOffice: number;
    phoneInternet: number;
    mileage: number;
    cost_security?: number;
    cost_cleaning?: number;
    cost_utilities?: number;
    cost_rent?: number;
    cost_rent_insurance?: number;
    cost_HOA?: number;
    cost_home_insurance?: number;
    cost_property_taxes?: number;
    cost_mortgage_interest?: number;
    cost_internet?: number;
    cost_phone?: number;
    businessMiles?: number;
    phoneInternetBusinessPercent?: number;
};

interface MonthlyExpenseCardsProps {
    userAnswers: UserAnswers;
    onFillOutCurrentMonth?: () => void;
    onFillOutPreviousMonth?: () => void;
    onExpenseDataChange?: (data: MonthlyData) => void;
}

function MonthlyExpenseCards({
    userAnswers,
    onFillOutCurrentMonth = () => {},
    onFillOutPreviousMonth = () => {},
    onExpenseDataChange = () => {},
}: MonthlyExpenseCardsProps) {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('');
    const { data: member } = useMember();

    const { currentMonth, previousMonth, showPreviousMonth } = useMemo(() => {
        const now = dayjs();
        const currentMonthName = now.format('MMMM');

        // Get previous month
        const previousDate = now.subtract(1, 'month');
        const previousMonthName = previousDate.format('MMMM');

        // Check if entity was formed in the current month
        let shouldShowPreviousMonth = true;
        if (member?.establisheddate) {
            const entityFormationDate = dayjs(member.establisheddate);
            const currentMonthStart = now.startOf('month');
            const currentMonthEnd = now.endOf('month');

            // If entity was formed in the current month, don't show previous month
            if (entityFormationDate.isAfter(currentMonthStart) && entityFormationDate.isBefore(currentMonthEnd)) {
                shouldShowPreviousMonth = false;
            }
        }

        return {
            currentMonth: currentMonthName,
            previousMonth: previousMonthName,
            showPreviousMonth: shouldShowPreviousMonth,
        };
    }, [member?.establisheddate]);

    // Simple data storage - just the final amounts for each month
    const [monthlyData, setMonthlyData] = useState<MonthlyData>({});

    // Track which months were auto-filled
    const [autoFilledMonths, setAutoFilledMonths] = useState<Set<string>>(new Set());

    // Notify parent when expense data changes
    React.useEffect(() => {
        onExpenseDataChange(monthlyData);
    }, [monthlyData, onExpenseDataChange]);

    // Calculate total reimbursable amount and check if we have data
    const { totalReimbursableAmount, hasData } = useMemo(() => {
        const values = Object.values(monthlyData);
        const totalAmount = values.reduce((total, monthData) => {
            return total + (monthData.homeOffice || 0) + (monthData.phoneInternet || 0) + (monthData.mileage || 0);
        }, 0);
        return {
            totalReimbursableAmount: totalAmount,
            hasData: values.length > 0,
        };
    }, [monthlyData]);

    const handleFillOut = (month: string) => {
        setSelectedMonth(month);
        setModalOpen(true);
        // Call the appropriate callback if provided
        if (month === currentMonth && onFillOutCurrentMonth) {
            onFillOutCurrentMonth();
        } else if (month === previousMonth && onFillOutPreviousMonth) {
            onFillOutPreviousMonth();
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSaveModal = (amounts: MonthlyExpenseData) => {
        const currentData = monthlyData[selectedMonth];
        const areAmountsUpdated = currentData
            ? Object.keys(amounts).some(
                  (key: string) =>
                      currentData[key as keyof MonthlyExpenseData] !== amounts[key as keyof MonthlyExpenseData]
              )
            : true;

        if (areAmountsUpdated) {
            // remove selected month from autoFilledMonths on manual update
            setAutoFilledMonths((prev) => new Set([...prev].filter((item) => item !== selectedMonth)));

            setMonthlyData((prev) => {
                const updated = { ...prev };
                updated[selectedMonth] = amounts;

                // Copy data to other month if it doesn't have data yet and previous month is shown
                if (showPreviousMonth) {
                    const otherMonth = selectedMonth === currentMonth ? previousMonth : currentMonth;
                    if (!updated[otherMonth]) {
                        // Copy ALL the detailed expense data from the current month
                        updated[otherMonth] = {
                            ...amounts,
                            mileage: 0, // Don't copy mileage
                        };

                        // Mark the other month as auto-filled
                        setAutoFilledMonths((prevSet) => new Set([...prevSet, otherMonth]));
                    }
                }

                return updated;
            });
        }

        setModalOpen(false);
    };

    return (
        <Box pb={5}>
            <Stack
                direction={{ xs: 'column', md: showPreviousMonth ? 'row' : 'column' }}
                spacing={3}
                sx={{
                    width: '100%',
                    alignItems: showPreviousMonth ? 'stretch' : 'center',
                    justifyContent: showPreviousMonth ? 'flex-start' : 'center',
                }}>
                {showPreviousMonth && (
                    <Box sx={{ flex: 1 }}>
                        <MonthlyExpenseCard
                            month={previousMonth}
                            userAnswers={userAnswers}
                            homeOfficeAmount={monthlyData[previousMonth]?.homeOffice || 0}
                            phoneInternetAmount={monthlyData[previousMonth]?.phoneInternet || 0}
                            mileageAmount={monthlyData[previousMonth]?.mileage || 0}
                            isAutoFilled={autoFilledMonths.has(previousMonth)}
                            onFillOut={() => handleFillOut(previousMonth)}
                        />
                    </Box>
                )}
                <Box
                    sx={{
                        flex: showPreviousMonth ? 1 : 'none',
                        maxWidth: showPreviousMonth ? 'none' : '400px',
                        width: '100%',
                    }}>
                    <MonthlyExpenseCard
                        month={currentMonth}
                        userAnswers={userAnswers}
                        homeOfficeAmount={monthlyData[currentMonth]?.homeOffice || 0}
                        phoneInternetAmount={monthlyData[currentMonth]?.phoneInternet || 0}
                        mileageAmount={monthlyData[currentMonth]?.mileage || 0}
                        isAutoFilled={autoFilledMonths.has(currentMonth)}
                        onFillOut={() => handleFillOut(currentMonth)}
                    />
                </Box>
            </Stack>

            {/* Reimbursement Summary Component */}
            {hasData && <ReimbursementSummary totalAmount={totalReimbursableAmount} />}

            <MixedUseExpenseModal
                key={selectedMonth} // Force remount when month changes
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveModal}
                month={selectedMonth}
                year={dayjs().year()}
                userAnswers={userAnswers}
                existingData={monthlyData[selectedMonth]}
            />
        </Box>
    );
}

export default MonthlyExpenseCards;
